import { createContext, useContext, useEffect, useLayoutEffect, useState } from "react";

/**
 * ThemeProvider
 *
 * Manages light/dark/system theme state. Persists user choice in localStorage.
 * Applies `light` or `dark` class to <html> root for CSS-level theming.
 */
const ThemeContext = createContext({
  theme: "light",
  setTheme: () => {},
  resolvedTheme: "light",
});

const STORAGE_KEY = "crickhere_theme";
const VALID_THEMES = new Set(["light", "dark", "system"]);

const getStoredTheme = (fallback) => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return VALID_THEMES.has(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
};

const getSystemTheme = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";

const applyThemeClass = (themeName) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  root.classList.add(themeName);
  root.dataset.theme = themeName;
  root.style.colorScheme = themeName;
  // Update <meta name="theme-color"> for mobile browsers
  let meta = document.querySelector('meta[name="theme-color"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = themeName === "light" ? "#FFFFFF" : "#020617";
  if (document.body) {
    document.body.style.backgroundColor = themeName === "light" ? "#FFFFFF" : "#020617";
  }
};

export const ThemeProvider = ({ children, defaultTheme = "light" }) => {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === "undefined") return defaultTheme;
    return getStoredTheme(defaultTheme);
  });

  const [systemTheme, setSystemTheme] = useState(getSystemTheme);

  const resolvedTheme = theme === "system" ? systemTheme : theme;

  useLayoutEffect(() => {
    applyThemeClass(resolvedTheme);
  }, [resolvedTheme]);

  // Listen for system theme changes when in "system" mode
  useEffect(() => {
    if (theme !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: light)");
    const handler = () => setSystemTheme(getSystemTheme());
    media.addEventListener?.("change", handler);
    return () => media.removeEventListener?.("change", handler);
  }, [theme]);

  useEffect(() => {
    const syncAcrossWebViews = (event) => {
      if (event.key !== STORAGE_KEY || !VALID_THEMES.has(event.newValue)) return;
      setThemeState(event.newValue);
    };
    window.addEventListener("storage", syncAcrossWebViews);
    return () => window.removeEventListener("storage", syncAcrossWebViews);
  }, []);

  const setTheme = (next) => {
    if (!VALID_THEMES.has(next)) return;
    // Apply synchronously so native WKWebView reflects the tap immediately.
    const nextResolved = next === "system" ? getSystemTheme() : next;
    applyThemeClass(nextResolved);
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
